import { Link, useLocation } from 'react-router-dom'
import { Lock, Mail } from 'lucide-react'
import { findPageMetadata } from '@/config/pageMetadata'
import { useOrganizationContact } from '@/features/organization/hooks/useOrganizationContact'
import { useMe } from '@/features/settings/hooks/useMe'

function pageNameFor(pathname: string): string | null {
  return findPageMetadata(pathname)?.title.split(' | ')[0] ?? null
}

function accessRequestMailto(
  contactEmail: string,
  subjectName: string,
  pageUrl: string,
  accountEmail: string | undefined,
): string {
  const lines = [
    'Hi,',
    '',
    `I need access to ${subjectName}. Could you add this permission to my role?`,
    '',
    `Page: ${pageUrl}`,
    ...(accountEmail ? [`My account: ${accountEmail}`] : []),
  ]
  const subject = encodeURIComponent(`Access request: ${subjectName}`)
  return `mailto:${contactEmail}?subject=${subject}&body=${encodeURIComponent(lines.join('\n'))}`
}

export interface AccessDeniedProps {
  /** What the person tried to open. Defaults to the current page's name. */
  resourceName?: string
}

/**
 * Shown whenever a role doesn't include what a page or folder needs. It says who
 * can fix that, rather than moving the person somewhere else without a reason.
 */
export function AccessDenied({ resourceName }: AccessDeniedProps): React.ReactElement {
  const { pathname } = useLocation()
  const { data: contact } = useOrganizationContact()
  const { data: me } = useMe()

  const name = resourceName ?? pageNameFor(pathname)
  const contactEmail = contact?.contactEmail
  const pageUrl = `${window.location.origin}${pathname}`

  return (
    <div className="flex flex-1 items-center justify-center px-4 py-12">
      <div className="flex w-full max-w-md flex-col items-center text-center">
        <span className="rounded-full bg-gray-100 p-3 dark:bg-gray-700/50">
          <Lock className="h-6 w-6 text-gray-500 dark:text-gray-400" aria-hidden />
        </span>
        <h1 className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          You don&apos;t have access to {name ?? 'this page'}
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Your role doesn&apos;t include this permission. Contact your admin and ask them to add it,
          then refresh this page.
        </p>
        {contactEmail ? (
          <p className="mt-3 max-w-full truncate text-sm text-gray-600 dark:text-gray-400">
            Admin contact:{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">{contactEmail}</span>
          </p>
        ) : null}
        <div className="mt-6 flex items-center justify-center gap-2">
          {contactEmail ? (
            <a
              href={accessRequestMailto(contactEmail, name ?? pageUrl, pageUrl, me?.email)}
              className="inline-flex h-8 min-h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-primary px-3 text-sm font-medium text-white transition-colors hover:bg-primary/90"
            >
              <Mail className="h-4 w-4" aria-hidden />
              Email your admin
            </a>
          ) : null}
          <Link
            to="/dashboard"
            className="inline-flex h-8 min-h-8 items-center justify-center whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
          >
            Go to dashboard
          </Link>
        </div>
      </div>
    </div>
  )
}

AccessDenied.displayName = 'AccessDenied'
